/**
 * Perspective widget: streaming pivot table / chart powered by @finos/perspective.
 *
 * Two data modes:
 *   1. Regular dataset  : widget.dataset_id set. Rows are pulled via GET /datasets/:id/preview.
 *   2. Federated widget : widget.federated_spec set. Rows come from POST /federated/query.
 *
 * The user can pivot, aggregate and switch chart types inside the widget itself using
 * Perspective's built-in UI - so a single widget replaces many one-off charts.
 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle, Inbox } from "lucide-react";
import api from "@/lib/api";

// Perspective is a Web Component; importing it registers <perspective-viewer> globally.
// We lazy-load the whole bundle the first time the widget is mounted to keep the base
// bundle small (Perspective + WASM is ~1.5 MB gzipped).
let perspectiveReady = null;
function ensureLocaleSupported() {
  // Some environments (headless Chromium in test containers, older WebViews) expose
  // navigator.language as an Intl-unsupported tag (e.g. 'en-US@posix') which crashes
  // @finos/perspective-viewer-datagrid deep inside Intl.NumberFormat. Normalise it
  // once before Perspective loads.
  try {
    Intl.NumberFormat(navigator.language);
  } catch (e) {
    try {
      Object.defineProperty(navigator, "language", { value: "en-US", configurable: true });
      Object.defineProperty(navigator, "languages", { value: ["en-US"], configurable: true });
    } catch (err) { /* immutable in some browsers; safe to ignore */ }
  }
}
function ensurePerspective() {
  if (!perspectiveReady) {
    ensureLocaleSupported();
    perspectiveReady = Promise.all([
      import("@finos/perspective"),
      import("@finos/perspective-viewer"),
      import("@finos/perspective-viewer-datagrid"),
      import("@finos/perspective-viewer-d3fc"),
      import("@finos/perspective-viewer/dist/css/pro.css"),
    ]).then(([perspective]) => perspective.default);
  }
  return perspectiveReady;
}

async function loadRowsForWidget(widget) {
  // Federated widgets: run the cross-source query
  if (widget.federated_spec?.subqueries?.length && widget.federated_spec.combine_sql) {
    const { data } = await api.post("/federated/query", {
      subqueries: widget.federated_spec.subqueries,
      combine_sql: widget.federated_spec.combine_sql,
      limit: 100000,
    });
    return { columns: data.columns, rows: data.rows, engine: data.engine };
  }
  // Regular datasets: pull a preview slice
  if (widget.dataset_id) {
    const { data } = await api.get(
      `/datasets/${widget.dataset_id}/preview?limit=100000`,
    );
    return { columns: data.columns, rows: data.rows, engine: "preview" };
  }
  return { columns: [], rows: [], engine: null };
}

export function PerspectiveWidget({ widget, onConfigChange = null }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const tableRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [empty, setEmpty] = useState(false);

  const savedConfig = widget.config?.perspective_config || null;
  const configKey = JSON.stringify(widget.federated_spec || widget.dataset_id || "");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEmpty(false);

    (async () => {
      try {
        const perspective = await ensurePerspective();
        const { rows } = await loadRowsForWidget(widget);
        if (cancelled) return;
        if (!rows || rows.length === 0) {
          setEmpty(true);
          setLoading(false);
          return;
        }

        // Clean up previous instance
        if (tableRef.current) {
          try { await tableRef.current.delete(); } catch (e) { /* noop */ }
          tableRef.current = null;
        }
        if (viewerRef.current) {
          try { await viewerRef.current.delete(); } catch (e) { /* noop */ }
        }

        // Create a Perspective table from JSON rows
        const worker = perspective.worker();
        const table = await worker.table(rows);
        tableRef.current = table;

        // Mount the viewer element inside our container
        const el = document.createElement("perspective-viewer");
        el.setAttribute("data-testid", "perspective-viewer");
        // Force a stable locale — some browsers report an Intl-unsupported tag
        // (e.g. 'en-US@posix') which crashes perspective's datagrid formatter.
        el.setAttribute("locale", "en-US");
        el.style.width = "100%";
        el.style.height = "100%";
        containerRef.current.replaceChildren(el);
        viewerRef.current = el;

        await el.load(Promise.resolve(table));

        // Restore saved layout if any
        if (savedConfig) {
          try { await el.restore(savedConfig); } catch (e) { /* invalid config */ }
        }

        // Debounced persist: whenever the user pivots / changes columns / switches
        // renderer, save the new perspective config back onto the widget so it
        // survives dashboard reload. Only wired in the Builder (onConfigChange set).
        if (onConfigChange) {
          let saveTimer = null;
          const onUpdate = () => {
            if (saveTimer) clearTimeout(saveTimer);
            saveTimer = setTimeout(async () => {
              try {
                const cfg = await el.save();
                onConfigChange({ perspective_config: cfg });
              } catch (e) { /* viewer not ready */ }
            }, 800);
          };
          el.addEventListener("perspective-config-update", onUpdate);
          // Remember the listener for cleanup
          el._eip_onUpdate = onUpdate;
        }

        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error("Perspective widget failed", e);
        setError(e?.response?.data?.detail || e?.message || "Failed to load data");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // load/persist cycle is keyed by the serialized widget config
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (tableRef.current) tableRef.current.delete().catch(() => {});
      if (viewerRef.current) {
        const el = viewerRef.current;
        if (el._eip_onUpdate) el.removeEventListener("perspective-config-update", el._eip_onUpdate);
        el.delete().catch(() => {});
      }
    };
  }, []);

  // IMPORTANT: keep the container div mounted at all times so `containerRef.current`
  // is available inside the async effect. Loading / error / empty states are rendered
  // as overlays instead of replacing the container.
  return (
    <div
      data-testid="perspective-widget"
      className="relative h-full w-full overflow-hidden rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--glass-bg)]"
    >
      <div ref={containerRef} className="h-full w-full" />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[color:var(--glass-bg)]">
          <Loader2 className="w-5 h-5 animate-spin text-[color:var(--brand-ocean)]" />
        </div>
      )}
      {!loading && error && (
        <div className="absolute inset-0 flex items-center justify-center bg-[color:var(--glass-bg)]">
          <div className="text-center text-[color:var(--danger)] text-xs px-3">
            <AlertTriangle className="w-5 h-5 mx-auto mb-1" />
            {error}
          </div>
        </div>
      )}
      {!loading && !error && empty && (
        <div className="absolute inset-0 flex items-center justify-center bg-[color:var(--glass-bg)]">
          <div className="text-center text-[color:var(--ink-3)] text-xs">
            <Inbox className="w-5 h-5 mx-auto mb-1 opacity-60" />
            No data
          </div>
        </div>
      )}
    </div>
  );
}
